import './MarkdownEditor.scss';

import MDEditor from '@uiw/react-md-editor'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import rehypeSanitize from 'rehype-sanitize';
import { Services } from '../../api/Services';
import { debounce } from 'lodash';
import { Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import classNames from 'classnames';

type Props = {
  value?: string;
  onChange?: (value?: string) => void;
  onValidated?: (valid: boolean) => void;
  placeholder?: string;
  height?: string;
}

interface MarkdownStatus {
  valid: boolean;
  reason?: string;
}

export default function MarkdownEditor({ value, placeholder, onChange, onValidated, height }: Props) {
  const [markdownStatus, setMarkdownStatus] = useState<MarkdownStatus>();
  const [loading, setLoading] = useState(false);

  useEffect(() => onValidated?.(markdownStatus ? markdownStatus.valid : !loading), [markdownStatus, loading, onValidated]);

  const validateMarkdown = useCallback((value?: string) => {
    Services.validateMarkdown({ value })
      .then(
        () => {
          setLoading(false);
          setMarkdownStatus({ valid: true });
        },
        (err: any) => {
          console.warn("Error during markdown validation", err.response);
          setLoading(false);
          setMarkdownStatus(err.response ? { valid: false, reason: err.response.data.message } : undefined);
        }
      );
  }, []);

  const debounceMarkdownValidation = useMemo(() => debounce(validateMarkdown, 500), [validateMarkdown]);

  const handleMarkdownChange = useCallback((value?: string) => {
    onChange?.(value);
    setMarkdownStatus(undefined);
    if (value) {
      setLoading(true);
      debounceMarkdownValidation(value);
    }
    else setLoading(false);
  }, [debounceMarkdownValidation, onChange]);

  return (
    <div data-color-mode="light" className={classNames("markdown-editor", { "invalid": markdownStatus && !markdownStatus.valid })}>
      <MDEditor
        value={value || ""}
        onChange={handleMarkdownChange}
        previewOptions={{
          rehypePlugins: [[rehypeSanitize]],
        }}
        textareaProps={{ placeholder }}
        height={height}
      />
      <div className="markdown-validation-icon">
        {(loading || markdownStatus) && (
          <Tooltip title={markdownStatus?.reason || (loading ? "Loading" : undefined)}>
            <FontAwesomeIcon
              icon={loading ? solid('circle-notch') : markdownStatus?.valid ? regular('check-circle') : regular('times-circle')}
              className={classNames(markdownStatus ? { 'text-success': markdownStatus.valid, 'text-danger': !markdownStatus.valid } : 'text-dark')}
              spin={loading}
              size="sm"
            />
          </Tooltip>
        )}
      </div>
    </div>
  )
}